import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  closeDrawer,
  resetAppointmentSelections,
  resetSchedulingMode,
  updateCalendarFilters,
  updateCalendarView,
  updateSelectedDate,
  updateTimezone,
} from "@/redux/actions/calendar";
import { cn } from "@/lib/helpers/cn";
import { getLocalTimezone } from "@/redux/reducers/authReducer";
import { selectIsSchedulingOnBehalfOf } from "@/redux/selectors/calendarSelectors";
import { useCalendarDrawerDirection } from "@/utils/hooks/useCalendarDrawerDirection";

import { buildCalendarFilterFromPreferences } from "./utils/general";
import { calculateEventPositions } from "./utils/positions";
import { CalendarContainer } from "./components/Container/CalendarContainer";
import CalendarDrawer from "./components/Drawer/CalendarDrawer";
import { EventGroup } from "./components/Events/EventGroup";
import ScheduleNav from "./components/ScheduleNav/ScheduleNav";
import { scheduleToEvents } from "./utils/event";
import { useCalendarDimensions } from "./hooks/useCalendarDimensions";

export function Calendar({
  daysToDisplay,
  patientDropdownOptions: _patientDropdownOptions,
  schedule,
  unconfirmedEvent,
  workingHours,
}) {
  const calendarBackgroundRef = useRef();
  const stickyHeaderRef = useRef();
  const { calendarRect } = useCalendarDimensions(calendarBackgroundRef, stickyHeaderRef);

  const dispatch = useDispatch();
  const calendar = useSelector((state) => state.calendar);
  const profile = useSelector((state) => state.auth.profile);
  const isSchedulingOnBehalfOf = useSelector(selectIsSchedulingOnBehalfOf);
  // this class name is used to manually adjust the calendar components
  // to account for the header as the calendar becomes sticky
  // the amount of adjustment needed is dependent on the scheduling mode
  const className = isSchedulingOnBehalfOf ? "SchedulingBehalfOf" : "CalendarInPage";

  useEffect(() => {
    let prevTz = calendar.timezone;
    const checkTzChange = () => {
      const currentTz = getLocalTimezone();

      if (currentTz !== prevTz) {
        dispatch(updateTimezone(currentTz));
      }
    };

    const interval = setInterval(checkTzChange, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [calendar.timezone, dispatch]);

  useEffect(() => {
    // when the calendar unmounts, the appointment drawer should be closed
    // and the appointment selection should be reset to avoid stale state
    return () => {
      const currentUserPreferences = profile?.preferences;
      const currentUserCalendarPreferences =
        buildCalendarFilterFromPreferences(currentUserPreferences);
      const selectedDate = moment();

      dispatch(updateCalendarView(currentUserCalendarPreferences.viewType));
      dispatch(updateSelectedDate(selectedDate));
      dispatch(resetSchedulingMode());
      dispatch(updateCalendarFilters(currentUserCalendarPreferences.calendarFilters));
      dispatch(resetAppointmentSelections());
      dispatch(closeDrawer());
    };
  }, [dispatch, profile]);

  const createEventGroups = (scheduledEvents) => {
    const events = scheduleToEvents(scheduledEvents, calendar.timezone, daysToDisplay);

    return calculateEventPositions(events, daysToDisplay, calendarRect);
  };

  const calendarEvents = useMemo(() => {
    const eventGroups = createEventGroups(schedule);

    // Created separately so it can overlap over event groups
    if (unconfirmedEvent) {
      eventGroups.push(...createEventGroups([unconfirmedEvent]));
    }

    const eventGroupComponents = eventGroups.map((eventGroup, index) => {
      return (
        <EventGroup
          eventGroup={eventGroup}
          key={
            eventGroup.id ||
            `${eventGroup.startTime}-${eventGroup.position?.top}-${eventGroup.position?.left}-${index}`
          }
        />
      );
    });

    return eventGroupComponents;
  }, [schedule, calendarRect, daysToDisplay, calendar.timezone]);

  const direction = useCalendarDrawerDirection();
  let openClass = calendar.openDrawer ? "open" : "closed";

  const drawerComponent = (
    <CalendarDrawer
      calendarRect={calendarRect}
      className={`apti-CalendarAppointmentPickerDrawer-${openClass} ${className}`}
      direction={direction}
    />
  );

  return (
    <div className="Calendar">
      <ScheduleNav className={className} />

      <div
        className={cn(
          "CalendarWrapper",
          // the right border of the calendar
          // is usually added by the day slots, but
          // when the calendar drawer is open we must
          // add a border here because it cannot be added to
          // the calendar drawer since it is sticky and
          // and therefore it cannot always fill the entire
          // and have a border around it
          calendar.openDrawer ? "CalendarWrapper--add_border" : null,
        )}
      >
        <CalendarContainer
          className={className}
          daysToDisplay={calendar.selectedScheduleDates}
          ref={{ calendarBackgroundRef, stickyHeaderRef }}
          workingHours={workingHours}
        >
          {calendarEvents}
        </CalendarContainer>

        {direction === "right" && (
          <aside
            className={`sticky-drawer-container apti-CalendarAppointmentPickerDrawer-${openClass} ${className}`}
          >
            {drawerComponent}
          </aside>
        )}
      </div>

      {direction === "bottom" && drawerComponent}
    </div>
  );
}

Calendar.propTypes = {
  daysToDisplay: PropTypes.arrayOf(PropTypes.instanceOf(moment)),
  patientDropdownOptions: PropTypes.any,
  schedule: PropTypes.arrayOf(PropTypes.object),
  unconfirmedEvent: PropTypes.object,
  workingHours: PropTypes.object,
};

Calendar.defaultProps = {
  daysToDisplay: [moment().local()],
  schedule: [],
  unconfirmedEvent: null,
};
